import { useState } from "react";
import axios from "axios";
import { useEffect, useRef } from "react";
import { ExternalLink, Clock, Users, CheckCircle2, Loader2, ChevronDown } from "lucide-react";
import { DURATIONS, BOARDROOMS } from "../lib/boardrooms";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HoverGallery = ({ imageUrls, name, capacity }) => {
  const [idx, setIdx] = useState(0);
  const intervalRef = useRef(null);
  useEffect(() => () => clearInterval(intervalRef.current), []);

  const start = () => {
    if (imageUrls.length <= 1) return;
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => setIdx((i) => (i + 1) % imageUrls.length), 1400);
  };
  const stop = () => { clearInterval(intervalRef.current); setIdx(0); };

  return (
    <div
      className="group relative w-full aspect-[3/4] lg:aspect-auto lg:h-full lg:min-h-[560px] overflow-hidden bg-[#E6DFD3] cursor-pointer"
      onMouseEnter={start}
      onMouseLeave={stop}
      onClick={() => imageUrls.length > 1 && setIdx((i) => (i + 1) % imageUrls.length)}
    >
      {imageUrls.map((src, i) => (
        <img
          key={src + i}
          src={src}
          alt={`${name} — view ${i + 1}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
          loading="lazy"
        />
      ))}
      <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#F9F6F0]/90 backdrop-blur-sm px-3 py-1.5 rounded-sm z-10">
        <Users size={12} strokeWidth={1.5} className="text-[#5D4037]" />
        <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037]">{capacity}</span>
      </div>
      {imageUrls.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {imageUrls.map((_, i) => (
            <span key={i} className={`block w-1.5 h-1.5 rounded-full transition-colors duration-300 ${i === idx ? "bg-[#F9F6F0]" : "bg-[#F9F6F0]/40"}`} />
          ))}
        </div>
      )}
    </div>
  );
};

const initialForm = {
  name: "",
  email: "",
  phone: "",
  duration: DURATIONS[0].label,
  preferred_date: "",
  preferred_time: "",
  attendees: 1,
  message: "",
};

const inputCls =
  "w-full bg-transparent border-b border-[#E6DFD3] py-2.5 text-[#2D231E] font-sans text-sm placeholder-[#A88B6C] focus:outline-none focus:border-[#4A3C31] transition-colors rounded-none";
const labelCls =
  "block text-[10px] font-sans tracking-[0.25em] uppercase text-[#5D4037] mb-1.5";

const EnquiryForm = ({ boardroom, defaultDuration, onClose }) => {
  const [form, setForm] = useState({ ...initialForm, duration: defaultDuration || DURATIONS[0].label });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.preferred_date) {
      setError("Please fill in name, email and preferred date.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/boardroom-enquiries`, {
        ...form,
        attendees: Number(form.attendees) || 1,
        boardroom: boardroom.name,
        location: boardroom.location,
      });
      setSubmitted(true);
    } catch (err) {
      setError("Couldn't send your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="border-t border-[#E6DFD3] bg-[#F9F6F0] p-7 sm:p-8" data-testid={`boardroom-success-${boardroom.key}`}>
        <CheckCircle2 size={26} strokeWidth={1.5} className="text-[#8B7355] mb-3" />
        <h5 className="font-serif text-xl text-[#2D231E] mb-2">Request received.</h5>
        <p className="font-sans text-sm text-[#5D4037] max-w-md">
          Thanks — someone from the {boardroom.location.split(" · ")[1] || "Saba"} team will be in
          contact shortly to confirm <strong>{boardroom.name}</strong> for {form.duration.toLowerCase()} on {form.preferred_date}.
        </p>
        <button
          type="button"
          onClick={onClose}
          className="mt-5 inline-flex items-center justify-center px-5 py-2.5 bg-transparent border border-[#4A3C31] text-[#4A3C31] font-sans text-[10px] tracking-[0.25em] uppercase hover:bg-[#4A3C31] hover:text-[#F9F6F0] transition-colors rounded-sm"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid={`boardroom-enquiry-${boardroom.key}`}
      className="border-t border-[#E6DFD3] bg-[#F9F6F0] p-7 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5"
    >
      <div>
        <label className={labelCls} htmlFor={`name-${boardroom.key}`}>Name</label>
        <input id={`name-${boardroom.key}`} name="name" value={form.name} onChange={onChange} required className={inputCls} placeholder="Your name" />
      </div>
      <div>
        <label className={labelCls} htmlFor={`email-${boardroom.key}`}>Email</label>
        <input id={`email-${boardroom.key}`} name="email" type="email" value={form.email} onChange={onChange} required className={inputCls} placeholder="you@example.com" />
      </div>
      <div>
        <label className={labelCls} htmlFor={`phone-${boardroom.key}`}>Phone (optional)</label>
        <input id={`phone-${boardroom.key}`} name="phone" type="tel" value={form.phone} onChange={onChange} className={inputCls} placeholder="04xx xxx xxx" />
      </div>
      <div>
        <label className={labelCls} htmlFor={`attendees-${boardroom.key}`}>Attendees</label>
        <input id={`attendees-${boardroom.key}`} name="attendees" type="number" min={1} max={boardroom.attendeesMax} value={form.attendees} onChange={onChange} required className={inputCls} />
      </div>
      <div>
        <label className={labelCls} htmlFor={`duration-${boardroom.key}`}>Duration</label>
        <select id={`duration-${boardroom.key}`} name="duration" value={form.duration} onChange={onChange} required className={`${inputCls} appearance-none bg-[#F9F6F0]`}>
          {DURATIONS.map((d) => <option key={d.id} value={d.label}>{d.label}</option>)}
        </select>
      </div>
      <div>
        <label className={labelCls} htmlFor={`date-${boardroom.key}`}>Preferred date</label>
        <input id={`date-${boardroom.key}`} name="preferred_date" type="date" value={form.preferred_date} onChange={onChange} required className={inputCls} />
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls} htmlFor={`time-${boardroom.key}`}>Preferred start time</label>
        <input id={`time-${boardroom.key}`} name="preferred_time" type="time" value={form.preferred_time} onChange={onChange} className={inputCls} />
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls} htmlFor={`msg-${boardroom.key}`}>Message (optional)</label>
        <textarea id={`msg-${boardroom.key}`} name="message" rows={3} value={form.message} onChange={onChange} placeholder="Anything we should know? Catering, AV, accessibility…" className={`${inputCls} resize-none`} />
      </div>
      {error && <p className="sm:col-span-2 font-sans text-sm text-[#9D5B5B]">{error}</p>}
      <div className="sm:col-span-2 flex items-center justify-end gap-3 pt-1">
        <button type="button" onClick={onClose} className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037] hover:text-[#2D231E]">
          Cancel
        </button>
        <button type="submit" disabled={submitting} className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-[#4A3C31] text-[#F9F6F0] font-sans text-[11px] tracking-[0.3em] uppercase hover:bg-[#2D231E] transition-colors rounded-sm disabled:opacity-60">
          {submitting && <Loader2 size={12} className="animate-spin" />}
          {submitting ? "Sending…" : "Send Request"}
        </button>
      </div>
    </form>
  );
};

const BoardroomCard = ({ boardroom }) => {
  const { key, name, capacity, blurb, hourlyRate, imageUrls = [] } = boardroom;
  const [openDuration, setOpenDuration] = useState(null);
  const hasImages = imageUrls.length > 0;

  return (
    <div data-testid={`boardroom-card-${key}`} className="bg-[#FAF9F6] border border-[#E6DFD3] rounded-sm overflow-hidden">
      <div className={`grid grid-cols-1 ${hasImages ? "lg:grid-cols-12" : ""}`}>
        {hasImages && (
          <div className="lg:col-span-5">
            <HoverGallery imageUrls={imageUrls} name={name} capacity={capacity} />
          </div>
        )}
        <div className={`${hasImages ? "lg:col-span-7" : ""} p-7 sm:p-9`}>
          <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
            <div>
              <h4 className="font-serif text-2xl sm:text-3xl text-[#2D231E] mb-1">{name}</h4>
              {!hasImages && <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037] mb-2">{capacity}</p>}
              <p className="font-sans text-sm text-[#5D4037] max-w-md mt-2">{blurb}</p>
            </div>
            {hourlyRate ? (
              <div data-testid={`boardroom-rate-${key}`} className="inline-flex flex-col items-end shrink-0">
                <span className="font-serif text-2xl sm:text-3xl text-[#2D231E] leading-none">
                  ${hourlyRate}
                  <span className="font-sans text-xs tracking-[0.2em] uppercase text-[#8B7355] ml-1">/hr</span>
                </span>
                <span className="mt-1.5 inline-flex items-center gap-2 px-3 py-1 border border-[#E6DFD3] rounded-sm">
                  <Clock size={11} strokeWidth={1.5} className="text-[#5D4037]" />
                  <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037]">Enquiry to Book</span>
                </span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E6DFD3] rounded-sm shrink-0">
                <Clock size={12} strokeWidth={1.5} className="text-[#5D4037]" />
                <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037]">Enquiry to Book</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-3.5">
            {DURATIONS.map((d) => {
              const price = hourlyRate ? hourlyRate * d.hours : null;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setOpenDuration(d.label)}
                  data-testid={`enquire-${key}-${d.id}`}
                  className="group flex flex-col items-start justify-between p-4 bg-[#F9F6F0] border border-[#E6DFD3] rounded-sm hover:bg-[#4A3C31] hover:border-[#4A3C31] transition-colors duration-300 min-h-[104px] text-left"
                >
                  <span className="font-serif text-base sm:text-lg text-[#2D231E] group-hover:text-[#F9F6F0] transition-colors leading-tight">{d.label}</span>
                  {price !== null && (
                    <span className="font-serif text-lg text-[#8B7355] group-hover:text-[#C4A484] transition-colors mt-1">
                      ${price}
                    </span>
                  )}
                  <span className="mt-2 inline-flex items-center gap-1.5 font-sans text-[10px] tracking-[0.25em] uppercase text-[#8B7355] group-hover:text-[#C4A484] transition-colors">
                    Enquire to Book <ChevronDown size={11} strokeWidth={1.5} />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {openDuration && (
        <EnquiryForm
          boardroom={boardroom}
          defaultDuration={openDuration}
          onClose={() => setOpenDuration(null)}
        />
      )}
    </div>
  );
};

export const BoardroomHire = ({ locationKey, locationLabel }) => {
  const boardrooms = BOARDROOMS[locationKey] || [];
  const isMulti = boardrooms.length > 1;
  return (
    <section data-testid={`boardroom-hire-${locationKey}`} id="boardroom-hire" className="space-y-6 sm:space-y-8">
      <div>
        <h3 className="font-serif text-2xl sm:text-3xl text-[#2D231E]">{locationLabel}</h3>
        <p className="font-sans text-sm sm:text-base text-[#5D4037] max-w-xl mt-2">
          {isMulti
            ? "Two boardrooms to suit different group sizes — pick a duration to request your booking."
            : "Pick a duration to request your booking and we'll come back to confirm."}
        </p>
      </div>
      <div className="space-y-6 sm:space-y-8">
        {boardrooms.map((b) => <BoardroomCard key={b.key} boardroom={b} />)}
      </div>
    </section>
  );
};
